import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useHistory, useLocation } from "react-router-dom";
import { API_ENDPOINT } from "../../utils/constants";
import "react-phone-input-2/lib/style.css";
import FormStatus from "../components/FormStatus";
import { LinkedIn } from "react-linkedin-login-oauth2";
// import AppleLogin from "react-apple-login";
import LinkedInIcon from "../../assets/svg/linkedin.svg";
import GoogleIcon from "../../assets/svg/googleIcon.svg";
// import AppleIcon from "../../assets/svg/apple.svg";
import {
  GOOGLE_CLIENT_ID,
  LINKEDIN_CLIENT_ID,
  // APPLE_CLIENT_ID,
  // APPLE_RE_DIRECT_URI,
  LINKEDIN_RE_DIRECT_URI,
} from "../../utils/constants";

// Declare Google Identity Services types
declare global {
  interface Window {
    google: any;
  }
}

function LoginWithSocials() {
  const history = useHistory();
  const location = useLocation();
  const [alertStyle, setAlertStyle] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [messages, setMessages] = useState<any>({ type: "", message: "" });

  const [googleLoginResponse, setGoogleLoginResponse] = useState<{
    status: "success" | "fail";
    message: string;
  }>();

  // Detect if we're on login or register page
  const isLoginPage = location.pathname.includes("/login");
  const isRegisterPage = location.pathname.includes("/register");

  // Initialize Google Identity Services when component mounts
  useEffect(() => {
    if (window.google && GOOGLE_CLIENT_ID) {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response: any) => {
          if (response.credential) {
            // Decode JWT to get user info (without verification, backend will verify)
            try {
              const base64Url = response.credential.split('.')[1];
              const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
              const jsonPayload = decodeURIComponent(
                atob(base64)
                  .split('')
                  .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                  .join('')
              );
              const userInfo = JSON.parse(jsonPayload);
              
              await handleGoogleCallback(userInfo, response.credential);
            } catch (error: any) {
              console.error("Error decoding JWT:", error);
              setGoogleLoginResponse({
                status: "fail",
                message: "Failed to process Google authentication. Please try again.",
              });
            }
          } else if (response.error) {
            if (response.error === "popup_closed_by_user" || response.error === "popup_blocked") {
              console.log("User closed or blocked the Google OAuth popup");
              return;
            }
            
            setGoogleLoginResponse({
              status: "fail",
              message: "Google authentication failed. Please try again.",
            });
          }
        },
      });
    }
  }, []);

  const handleGoogleAuth = () => {
    if (!window.google) {
      console.error("Google Identity Services not loaded");
      setGoogleLoginResponse({
        status: "fail",
        message: "Google authentication service not available. Please refresh the page.",
      });
      return;
    }

    // Use Google Identity Services OAuth2 flow to get access token
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: 'openid profile email',
      callback: async (tokenResponse: any) => {
        if (tokenResponse.error) { 
          if (tokenResponse.error === "popup_closed_by_user") {
            console.log("User closed the Google OAuth popup");
            return;
          }
          
          setGoogleLoginResponse({
            status: "fail",
            message: "Google authentication failed. Please try again.",
          });
          return;
        }

        // Get user info using access token
        try {
          const userInfoResponse = await fetch(
            `https://www.googleapis.com/oauth2/v2/userinfo?access_token=${tokenResponse.access_token}`
          );
          const userInfo = await userInfoResponse.json();
          // Backend can handle access tokens now
          await handleGoogleCallback(userInfo, tokenResponse.access_token);
        } catch (error: any) {
          console.error("Error fetching user info:", error);
          setGoogleLoginResponse({
            status: "fail",
            message: "Failed to get user information. Please try again.",
          });
        }
      },
    });

    // Request access token with consent prompt
    client.requestAccessToken({ prompt: 'consent' });
  };

  const handleGoogleCallback = async (userInfo: any, token: string) => {
    const email = userInfo.email;
    const givenName = userInfo.given_name || userInfo.name?.split(' ')[0] || '';
    const familyName = userInfo.family_name || userInfo.name?.split(' ').slice(1).join(' ') || '';
    
    // token can be either ID token (JWT) or access token
    const tokenId = token;

    const urlSearchParams = new URLSearchParams(history.location.search);
    const redirectUrl: any = urlSearchParams.get("redirect_to");

    // Use unified Google auth endpoint - handles both login and signup
    const intendedRole = new URLSearchParams(redirectUrl || "").get("intended_role");
    try {
      const authRes = await fetch(API_ENDPOINT + `/google-auth/?intended_role=${encodeURIComponent(intendedRole || "user")}`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "client-type": "web",
        },
        body: JSON.stringify({
          email: email,
          google_auth_token: tokenId,
          first_name: givenName,
          last_name: familyName,
        }),
      });

      // Check if response is ok
      if (!authRes.ok) {
        const errorData = await authRes.json().catch(() => ({ message: "Unknown error" }));
        setMessages({
          type: "error",
          message: errorData?.message || errorData?.error?.[0] || "Google authentication failed. Please try again.",
        });
        return;
      }

      const authData = await authRes.json();
      // Check if request was successful
      if (!authData?.success) {
        console.error("API returned success: false");
        setMessages({
          type: "error",
          message: authData?.message || authData?.error?.[0] || "Google authentication failed. Please try again.",
        });
        return;
      }

      // Check verification status
      if (authData?.data?.google_auth_token_verification_status) {
        const isNewUser = authData?.data?.new_user;
        const successMessage = isNewUser 
          ? "Successfully signed up and logged in with Google" 
          : "Successfully logged in with Google";

        if (redirectUrl) {
          try {
            const redirectTo = new URL(decodeURIComponent(redirectUrl));
            window.location.href = redirectTo.href;
          } catch (e) {
            window.location.href = redirectUrl;
          }
        } else {
          history.push({
            pathname: "/",
            state: {
              response: true,
              message: successMessage,
            },
          });
        }
      } else {
        console.error("Token verification failed");
        setMessages({
          type: "error",
          message: authData?.message || "Google authentication failed. Please try again.",
        });
      }
    } catch (error: any) {
      console.error("Google auth error:", error);
      setMessages({
        type: "error",
        message: "Google authentication failed. Please try again.",
      });
    }
  };

  const linkedinHandleResponse = async (code: any) => {
    console.log({ code }, "linkedin");
    await fetch(API_ENDPOINT + "/linkedin-sign-up", {
      method: "POST",
      credentials: "include",
      body: JSON.stringify({
        code: code,
      }),
    })
      .then((res: any) => res.json())
      .then((data) => {
        console.log(data?.data?.refresh_token, "linkedin data");
        if (data?.data?.refresh_token) {
          const urlSearchParams = new URLSearchParams(history.location.search);
          const redirectUrl: any = urlSearchParams.get("redirect_to");
          let redirectTo = new URL(decodeURIComponent(redirectUrl));
          if (redirectUrl !== null) {
            window.location.href = redirectTo.href;
          } else {
            history.push({
              pathname: "/",
              state: {
                response: true,
                message: "Login with Linkedin Successfully",
              },
            });
          }
        } else {
          setMessages({
            type: "error",
            message: "Login fail, Please try again",
          });
        }
      });
  };

  // Apple ID login handler - DISABLED
  // const appleHandleResponse = async (response: any) => {
  //   await fetch(API_ENDPOINT + "/apple-sign-up", {
  //     method: "POST",
  //     credentials: "include",
  //     body: JSON.stringify({
  //       user: "",
  //       email: response?.user?.email,
  //       fullName: {
  //         firstName: response?.user?.name?.firstName || null,
  //         lastName: response?.user?.name?.lastName || null,
  //       },
  //       realUserStatus: 1,
  //       nonce: "",
  //       identityToken: response?.authorization?.id_token,
  //       authorizationCode: response?.authorization?.code,
  //       clientId: APPLE_CLIENT_ID,
  //     }),
  //   })
  //     .then((res: any) => res.json())
  //     .then((data) => {
  //       if (data?.data?.refresh_token) {
  //         const urlSearchParams = new URLSearchParams(history.location.search);
  //         const redirectUrl: any = urlSearchParams.get("redirect_to");
  //         let redirectTo = new URL(decodeURIComponent(redirectUrl));
  //         if (redirectUrl !== null) {
  //           window.location.href = redirectTo.href;
  //         } else {
  //           history.push({
  //             pathname: "/",
  //             state: {
  //               response: true,
  //               message: "Login with Apple Successfully",
  //             },
  //           });
  //         }
  //       } else {
  //         setMessages({
  //           type: "error",
  //           message: "Login fail, Please try again",
  //         });
  //       }
  //     })
  //     .catch((error) => {
  //       setMessages({
  //         type: "error",
  //         message: "Login fail, Please try again",
  //       });
  //     });
  // };

  useEffect(() => {
    messages.message ? setShowAlert(true) : setShowAlert(false);
  }, [messages]);

  useEffect(() => {
    messages?.type === "success" ? setAlertStyle(true) : setAlertStyle(false);
  }, [messages]);

  return (
    <div style={{ width: "100%", marginTop: "20px" }}>
      <SocialIconContainer>
        {/* Google Sign-In Button using Google Identity Services */}
        <img
          src={GoogleIcon}
          alt="google icon"
          onClick={handleGoogleAuth}
          style={{ cursor: "pointer" }}
          title={isLoginPage ? "Sign in with Google" : "Sign up with Google"}
        />
        {/* <LinkedIn
          clientId={LINKEDIN_CLIENT_ID}
          redirectUri={LINKEDIN_RE_DIRECT_URI}
          scope="r_liteprofile,r_emailaddress"
          onSuccess={linkedinHandleResponse}
          onError={(error: any) => {
            setMessages({
              type: "error",
              message: `${error}`,
            });
          }}
        >
          {({ linkedInLogin }: any) => (
            <img
              src={LinkedInIcon}
              onClick={linkedInLogin}
              style={{ cursor: "pointer" }}
              alt="linkedin"
            />
          )}
        </LinkedIn> */}
        {/* Apple ID login - DISABLED */}
        {/* <AppleLogin
          clientId={APPLE_CLIENT_ID}
          redirectURI={APPLE_RE_DIRECT_URI}
          callback={(response: any) => appleHandleResponse(response)}
          scope="name email"
          responseMode="form_post"
          usePopup={true}
          render={(renderProps) => (
            <img
              src={AppleIcon}
              onClick={renderProps.onClick}
              style={{ cursor: "pointer" }}
              alt="apple"
            />
          )}
        /> */}
      </SocialIconContainer>
      {showAlert && (
        <div
          className={alertStyle ? "alert alert-success " : "alert alert-danger"}
          role="alert"
        >
          {messages?.message}
        </div>
      )}
      {googleLoginResponse && (
        <FormStatus align="center" className={googleLoginResponse.status}>
          {googleLoginResponse.message}
        </FormStatus>
      )}
    </div>
  );
}

export default LoginWithSocials;

const SocialIconContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 30px;
  margin: 0 auto;
  @media (max-width: 576px) {
    gap: 20px;
  }
`;
